import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import ChartCard from "../../components/ChartCard";
import SegmentedToggle from "../../components/SegmentedToggle";
import { EmptyChartNote } from "../../components/charts/StackedColumns";
import HorizontalBars from "../../components/charts/HorizontalBars";
import { NoteCard } from "../../components/Layout";
import { useDashboardData } from "../../hooks/useDashboardData";
import { fetchLeaderboard } from "../../dashboardApi";
import { formatCents } from "../../format";
import { dashboardPath } from "../../routes";
import { viz } from "../../styles";
import { LeaderboardResponse, RevenueWindow } from "../../types";
import { useRevenueBasis } from "../../revenueBasis";

/**
 * Top 10 customers by Markit revenue over the selected window, from the nightly
 * rollup.
 *
 * Sorted and limited server-side off `adminCustomerStats`, not by fetching
 * every customer's stats and sorting in the browser — this card shows ten rows
 * no matter how many customers exist, so the query should cost ten rows too.
 *
 * **The window is a refetch, not a client-side reslice**, which is why it is
 * wired the way the volume chart's day/week/month toggle deliberately is not.
 * That toggle regroups rows already in the browser. This one changes which ten
 * customers Firestore returns: the top ten by trailing-30d revenue are a
 * different ten from the top ten by lifetime, and the browser cannot derive one
 * from the other without holding every customer.
 *
 * **Defaults to lifetime**, unlike `TopSendersCard`'s 30d. This board is the
 * "who has been worth the most to Markit" board and was lifetime-only before
 * Sep 2026; changing what it says on load would rewrite an answer people
 * already read here.
 */
const SERIES = [
  { key: "subscription", label: "Subscription", color: viz.series1 },
  { key: "ticketing", label: "Ticketing fees", color: viz.series2 },
  { key: "other", label: "Other fees", color: viz.series4 },
];

const WINDOWS: { key: RevenueWindow; label: string }[] = [
  { key: "30d", label: "30d" },
  { key: "90d", label: "90d" },
  { key: "lifetime", label: "All time" },
];

/** Fills the card title and the churned note, so the two never disagree. */
const WINDOW_COPY: Record<
  RevenueWindow,
  { title: string; period: string; empty: string }
> = {
  "30d": {
    title: "last 30 days",
    period: "trailing 30 days",
    empty: "No customer has earned Markit anything in the last 30 days.",
  },
  "90d": {
    title: "last 90 days",
    period: "trailing 90 days",
    empty: "No customer has earned Markit anything in the last 90 days.",
  },
  lifetime: {
    title: "lifetime",
    period: "all time",
    empty: "The nightly rollup hasn't recorded any revenue yet.",
  },
};

const CustomerLeaderboard = () => {
  const navigate = useNavigate();
  const [window, setWindow] = useState<RevenueWindow>("lifetime");
  const basis = useRevenueBasis();

  // The basis is a REFETCH, exactly like the window and for the same reason:
  // it changes which ten customers Firestore returns, not just how their bars
  // are labelled. Markit keeps ~96% of a subscription dollar and ~68% of a
  // ticketing one, so a ticketing-heavy creator genuinely ranks lower net.
  const fetcher = useCallback(
    () => fetchLeaderboard(window, basis),
    [window, basis]
  );
  const { data, error, loading, refreshing } =
    useDashboardData<LeaderboardResponse>(fetcher, [window, basis]);

  // Label off the response where there is one, so a card that is still showing
  // the previous window's bars is captioned with the window those bars are.
  const shown = data?.window ?? window;
  // Same rule for the basis, and it matters more: while a net refetch is in
  // flight the bars on screen are still the gross ordering, and captioning
  // them "net" would be a wrong claim about the rows a reader can see.
  const shownBasis = data?.basis ?? basis;
  const isNet = shownBasis === "net";
  const copy = WINDOW_COPY[shown];

  const isCurrent = (state?: string) => state === "ACTIVE" || state === "TRIAL";

  const rows = (data?.customers ?? [])
    // A customer who earned nothing in the window is not a "top customer" —
    // a zero-length bar with a name beside it reads as a measurement of
    // nothing rather than as the absence of one. Only ever trims the board on
    // a trailing window, where a real zero is common; on lifetime it fires
    // only if Markit has fewer than ten paying customers ever.
    .filter(entry => (isNet ? entry.netRevenueCents : entry.totalRevenueCents) > 0)
    .map(entry => ({
      // The bar's category label. Falls back to the uid so a customer with no
      // profile name still appears rather than showing an unlabeled bar.
      // A churned earner is marked inline — see the note below the chart.
      name:
        (entry.fullName || entry.username || entry.uid.slice(0, 8)) +
        (entry.subscriptionState !== undefined &&
        !isCurrent(entry.subscriptionState)
          ? " ·"
          : ""),
      uid: entry.uid,
      // Each segment nets its OWN stream's fee rather than taking a share of
      // one blended total, so the three segments still add up to the row's net
      // figure and the stack stays readable as the same three things it was.
      subscription: isNet
        ? entry.subscriptionRevenueCents - entry.subscriptionStripeFeeCents
        : entry.subscriptionRevenueCents,
      ticketing: isNet
        ? entry.ticketingFeeRevenueCents - entry.ticketingStripeFeeCents
        : entry.ticketingFeeRevenueCents,
      other: isNet
        ? entry.otherFeeRevenueCents - entry.otherStripeFeeCents
        : entry.otherFeeRevenueCents,
      total: isNet ? entry.netRevenueCents : entry.totalRevenueCents,
    }));

  // Revenue reaching back to 2021 means this board can now rank creators who
  // have since churned — real revenue, but not current customers, and this card
  // sits directly above a table whose subtitle says "active or trialing".
  //
  // Counted over the rows actually drawn, not over the raw response: a
  // zero-revenue customer trimmed above is not on this board, so counting them
  // here would put a number in the note that a reader cannot find in the chart.
  const churned = (data?.customers ?? []).filter(
    entry =>
      (isNet ? entry.netRevenueCents : entry.totalRevenueCents) > 0 &&
      entry.subscriptionState !== undefined &&
      !isCurrent(entry.subscriptionState)
  );

  const card = (
    <ChartCard
      title={`Top 10 customers by Markit revenue, ${
        isNet ? "net" : "gross"
      } (${copy.title})`}
      subtitle={
        isNet
          ? `Nightly Stripe rollup · subscription invoices + ticketing and other fees, LESS Stripe's processing cut · ${copy.period}, including creators who have since churned`
          : `Nightly Stripe rollup · subscription invoices + ticketing and other fees · ${copy.period}, including creators who have since churned`
      }
      series={SERIES}
      dimmed={refreshing}
      freshness={{
        kind: "cached",
        updatedAt: data?.sync.lastRunFinishedAt ?? undefined,
      }}
      action={
        <SegmentedToggle
          label="Window"
          options={WINDOWS}
          value={window}
          onChange={setWindow}
        />
      }
      table={{
        categoryLabel: "Customer",
        rows: rows.map(row => ({
          category: row.name,
          values: {
            subscription: formatCents(row.subscription),
            ticketing: formatCents(row.ticketing),
            other: formatCents(row.other),
          },
        })),
      }}
    >
      {error ? (
        <EmptyChartNote label={error} />
      ) : loading ? (
        <EmptyChartNote label="Loading…" />
      ) : rows.length === 0 ? (
        <EmptyChartNote label={copy.empty} />
      ) : (
        <HorizontalBars
          data={rows}
          series={SERIES}
          categoryKey="name"
          // Creator and business names run long; a clipped axis label turns one
          // creator's name into another plausible one.
          categoryWidth={110}
          // The underlying figures are CENTS. Unformatted, the axis read
          // "800000" for $8,000 and a segment label read "689065" for
          // $6,890.65 — numbers that look like dollars and are wrong by a
          // factor of a hundred. Both wear currency now.
          valueTickFormatter={value => formatCents(value)}
          valueLabelFormatter={value => formatCents(value)}
          height={Math.max(200, rows.length * 32)}
          onBarClick={row => navigate(dashboardPath.customer(row.uid))}
        />
      )}
    </ChartCard>
  );

  // One tree shape whether or not the note renders, which matters now that the
  // window toggle can turn the note on and off inside a session: returning
  // `card` bare in one branch and `<div>{card}…</div>` in the other gives React
  // a different element type at this position, so it unmounts and remounts
  // `ChartCard` — silently resetting its own chart/table view. Switching from
  // lifetime (4 churned, note shown) to 30d (none, note hidden) threw you out of
  // the table you were reading. Same shape TopSendersCard already uses.
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {card}
      {isNet && (
        <NoteCard title="This is a different ten people from the gross board">
          Net subtracts Stripe&rsquo;s own processing cut, read per charge from{" "}
          <code>balance_transaction.fee</code> &mdash; and the two revenue
          streams keep very different shares of a dollar. Markit keeps roughly{" "}
          <strong>96%</strong> of a subscription payment and roughly{" "}
          <strong>68%</strong> of a ticketing fee, because Markit is merchant of
          record on the <em>whole</em> ticket price: Stripe charges its
          percentage on the full charge while Markit keeps only its application
          fee out of it. So a creator whose revenue is mostly ticketing ranks
          lower here than on the gross board, and the board is re-sorted
          server-side rather than relabelled &mdash; a net column under a gross
          ordering would look right and be wrong. Stripe keeps its fee on
          refunded charges, so a refund reduces the revenue and not the fee.
        </NoteCard>
      )}
      {churned.length > 0 && (
        <NoteCard title={`${churned.length} of these are no longer customers`}>
          Marked with a &middot; after the name.{" "}
          {shown === "lifetime" ? (
            <>
              This board ranks <strong>lifetime</strong> revenue, which since
              the Sep 2026 backfill reaches back to Markit&rsquo;s first Stripe
              charge in Dec 2021 &mdash; so it includes creators who earned real
              money and have since churned. They are kept rather than filtered,
              because scoping lifetime revenue to today&rsquo;s subscribers
              would delete Markit&rsquo;s own earning history from its own
              dashboard.
            </>
          ) : (
            <>
              This board ranks revenue over the <strong>{copy.period}</strong>,
              and a creator can bill Markit money inside that window and cancel
              before it ends &mdash; a churned name here means money that
              genuinely landed recently, not history. They are kept rather than
              filtered for the same reason the lifetime board keeps them: what
              Markit earned is what Markit earned.
            </>
          )}{" "}
          The table below this card is a different thing: current active and
          trialing customers only.{" "}
          {churned
            .map(
              entry =>
                `${entry.fullName || entry.username || entry.uid.slice(0, 8)} (${
                  entry.subscriptionState || "no account"
                })`
            )
            .join(", ")}
          .
        </NoteCard>
      )}
    </div>
  );
};

export default CustomerLeaderboard;
